// ─── Simulator: chat with the assistant without touching WhatsApp ───────────
//
// The point of this tab is to answer "would it say something stupid?" BEFORE a
// real customer finds out. So it runs the exact same prompt, knowledge and
// parser as a live message — the server shares one code path for both.
//
// Nothing here is a real conversation: the thread lives in this browser and is
// posted in full each turn. A simulator that wrote into the real conversation
// list would make the thing it exists to de-risk riskier.
//
// ─── THE SAME THREAD AS CONVERSATIONS (owner, 2026-10-01) ───────────────────
//
// « When I tested the simulator I sent an audio message » — the reply was
// generic, as if it heard nothing — « use the same simulator as the
// conversation — same everything: if they send images and videos I need to
// see them and play the videos, I need to be able to send images too, see
// them, delete them — like the Conversations WhatsApp view we have now. Use
// the same one. »
//
// So it is drawn by the panel's own ThreadFrame and Composer (chat/ChatThread):
// the same bubbles, pictures full size, playable videos, voice notes with
// their player and transcript, ⌘V a picture, the paperclip, the microphone.
// What differs is only what Send and Delete DO: a file goes to the panel's
// bucket (`simulateUpload`) and the turn to /simulate — which hears a voice
// note with the same transcriber as a real one, before the agent answers — and
// a delete changes this browser's thread, nothing else. Nothing goes to the
// relay, so nothing can reach WhatsApp (SimulatorTab.test.tsx).
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { whatsappApi, type SimulateUpload, type TeachProposal, type WhatsAppMessage } from '@/lib/api';
import { useT } from '@/lib/i18n';
import { SettingGroup } from './_ui';
import { ScenarioTests } from './ScenarioTests';
import { Composer, ThreadFrame, type BubbleOptions, type Outgoing } from './chat/ChatThread';
import { voiceForUpload } from './chat/wav';
import {
  applyResult, mediaPaths, newTurnId, restoreTurns, simKindForFile, teachMessages, toMessages, toPayload,
  type SimFile, type SimTurn,
} from './simulatorThread';

/** Switching tabs unmounts this component, so the thread has to live outside
 *  React state or a trip to the Brain tab throws the conversation away. Kept
 *  until the user explicitly ends it. */
const STORAGE_KEY = 'storiz_assistant_sim';

function loadTurns(): SimTurn[] {
  try { return restoreTurns(localStorage.getItem(STORAGE_KEY)); } catch { return []; }
}

function saveTurns(turns: SimTurn[]): void {
  // Files are uploaded and named by path, so the thread is words and links —
  // small. A browser that refuses storage still keeps the thread in memory.
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(turns)); } catch { /* in-memory thread still works */ }
}

const fileOf = (up: SimulateUpload): SimFile => ({
  path: up.path, url: up.url ?? '', signedAt: Date.now(), kind: up.kind, mime: up.mime, filename: up.filename,
});

/** Links last an hour; refreshed well before, and on every reload. */
const LINK_REFRESH_MS = 45 * 60_000;

export function SimulatorTab() {
  const { t } = useT();
  const qc = useQueryClient();
  const [turns, setTurns] = useState<SimTurn[]>(loadTurns);
  // The send runs after an upload, so it reads the thread as it is THEN.
  const turnsRef = useRef(turns);
  useEffect(() => { turnsRef.current = turns; }, [turns]);
  const [correcting, setCorrecting] = useState<string | null>(null);
  const [correction, setCorrection] = useState('');
  const [proposal, setProposal] = useState<TeachProposal | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  useEffect(() => { saveTurns(turns); }, [turns]);
  /**
   * Which real conversation this simulation runs against.
   *
   * ⚠️ THE DIFFERENCE BETWEEN A DEMO AND A TEST. Without a chat the simulator
   * calls the model with the prompt and the conversation and nothing else,
   * while the live agent is handed fifteen fact blocks — the order, the photos
   * actually received, the intake step, the payment stage, an open complaint.
   * Picking a customer here is what makes the answer the one they would really
   * get.
   */
  const [simChatId, setSimChatId] = useState<string | null>(null);
  const chats = useQuery({
    queryKey: ['wa-conversations'],
    queryFn: () => whatsappApi.conversations(),
    staleTime: 60_000,
  });

  // ─── Her files' links, kept fresh — the thread outlives them ─────────────
  const paths = mediaPaths(turns);
  const fresh = useQuery({
    queryKey: ['sim-media-urls', paths.join('|')],
    queryFn: () => whatsappApi.simulateMediaUrls(paths),
    enabled: paths.length > 0,
    staleTime: LINK_REFRESH_MS,
    refetchInterval: LINK_REFRESH_MS,
    // A new file changes the key: the links already fetched stay drawn meanwhile.
    placeholderData: (previous) => previous,
  });

  const simMut = useMutation({
    mutationFn: (thread: SimTurn[]) => whatsappApi.simulate(toPayload(thread), simChatId),
    onSuccess: (res) => setTurns((prev) => applyResult(prev, res, new Date().toISOString())),
  });

  /**
   * The composer's Send. A file goes to the panel's bucket first — the same
   * limits as a real send, refused on the composer's own « Échec de l'envoi »
   * line with what was composed kept — then the turn joins the thread and
   * /simulate answers it.
   */
  const send = async (o: Outgoing) => {
    const at = new Date().toISOString();
    const text = o.text.trim();
    const parts: SimTurn[] = [];
    if (o.voice) {
      // WAV when the browser can: what the transcriber always reads (chat/wav.ts).
      const audio = await voiceForUpload(o.voice);
      const up = await whatsappApi.simulateUpload({ file: audio.blob, filename: audio.filename, kind: 'voice' });
      parts.push({ id: newTurnId(), role: 'user', content: '', at, file: fileOf(up) });
      // A voice note carries no caption: words typed beside it follow as their
      // own message, the way the panel sends them (whatsapp-relay panelSend.js).
      if (text) parts.push({ id: newTurnId(), role: 'user', content: text, at });
    } else if (o.file) {
      const up = await whatsappApi.simulateUpload({ file: o.file, filename: o.file.name, kind: simKindForFile(o.file) });
      parts.push({ id: newTurnId(), role: 'user', content: text, at, file: fileOf(up) });
    } else {
      parts.push({ id: newTurnId(), role: 'user', content: text, at });
    }
    const next = [...turnsRef.current, ...parts];
    turnsRef.current = next;
    setTurns(next);
    simMut.mutate(next);
  };

  // ─── Delete: this browser's thread, nothing else ───────────────────────────
  // The way Conversations shows a delete — one question, then « Message
  // supprimé » — and, as live, a deleted message is no longer read by the agent.
  const askDelete = (m: WhatsAppMessage) => {
    if (!window.confirm(t('Supprimer ce message de la simulation ? Rien de réel n’est touché.'))) return;
    const now = new Date().toISOString();
    setTurns((prev) => prev.map((x) => (x.id === m.id ? { ...x, deletedAt: now } : x)));
    if (correcting === m.id) { setCorrecting(null); setProposal(null); setPreview(null); }
  };

  const correctingTurn = correcting ? turns.find((x) => x.id === correcting) ?? null : null;

  const teachMut = useMutation({
    mutationFn: (id: string) => whatsappApi.teach({
      messages: teachMessages(turns, id),
      wrong_reply: turns.find((x) => x.id === id)?.content ?? '',
      corrected_reply: correction.trim(),
    }),
    onSuccess: (res) => { setProposal(res.proposal); setPreview(res.preview_reply); },
  });

  const applyMut = useMutation({
    mutationFn: async () => {
      if (!proposal) return;
      if (proposal.mode === 'update' && proposal.target_id) {
        await whatsappApi.updateKnowledge(proposal.target_id, { title: proposal.title, body: proposal.body });
      } else {
        await whatsappApi.addKnowledge({ kind: 'instruction', title: proposal.title, body: proposal.body });
      }
    },
    onSuccess: () => {
      setProposal(null); setCorrecting(null); setCorrection(''); setPreview(null);
      // The Brain tab must not show a stale list after a rule was added.
      qc.invalidateQueries({ queryKey: ['assistant-knowledge'] });
    },
  });

  const startCorrecting = (id: string) => {
    setCorrecting(id);
    // Deliberately BLANK, not pre-filled with the old reply: this box takes an
    // instruction in your own words ("above trio use 299 x albums, keep it
    // short"), and pre-filling it invites editing the sentence instead.
    setCorrection('');
    setProposal(null);
    setPreview(null);
  };

  const byId = new Map(turns.map((x) => [x.id, x]));
  const messages = toMessages(turns, fresh.data?.urls ?? {}, t('(aucune réponse)'));

  /** The simulator's own lines on its bubbles. */
  const options = (m: WhatsAppMessage): BubbleOptions => {
    const turn = byId.get(m.id);
    if (!turn) return {};
    // Only the agent's reply (the bubble carrying its words) has tags and
    // « corriger » — correcting the customer's own words would teach it from
    // something nobody said.
    const reply = turn.role === 'assistant' && !turn.followsReply;
    return {
      onDelete: askDelete,
      deleteLabel: t('Supprimer de la simulation'),
      highlight: reply && correcting === turn.id,
      top: reply && (turn.factsUsed === false || turn.decidedWithoutModel) ? (
        <>
          {turn.factsUsed === false && (
            // The same warning as the picker, on the reply itself — a
            // conversation scrolls, and the banner is long gone by the fifth turn.
            <p className="text-[11px] text-amber-700">{t('sans les données du client')}</p>
          )}
          {turn.decidedWithoutModel && (
            <p className="text-[11px] text-gray-500">{t('réponse automatique — sans passer par l’IA')}</p>
          )}
        </>
      ) : null,
      bottom: reply ? (
        <>
          {turn.needsHuman && (
            <p className="mt-1 text-[11.5px] font-semibold text-amber-700 pr-14">
              {t('⚠ l’assistant demande un humain')}{turn.reason ? ` — ${turn.reason}` : ''}
            </p>
          )}
          <button type="button" onClick={() => startCorrecting(turn.id)} title={t('Cliquez pour corriger')}
            className="mt-1 inline-flex items-center gap-1 text-[11px] text-gray-500 hover:text-gray-800 cursor-pointer rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500">
            <svg aria-hidden viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="w-3 h-3">
              <path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            {t('corriger')}
          </button>
        </>
      ) : null,
    };
  };

  const simName = simChatId
    ? (() => {
      const cv = (chats.data?.conversations ?? []).find((x) => x.chat_id === simChatId);
      return cv?.name || simChatId.split('@')[0];
    })()
    : null;

  return (
    <div className="space-y-3">
      {/* ── THE CHAT IS THE POINT, SO THE READING IS NOT ─────────────────────
          Three stacked blocks of explanation sat between the tab and the thing
          it exists for. The first one is read once and then never again, so it
          collapses; what stays visible is the STATE — who is being simulated,
          and whether the answer will be a real one. ── */}
      <SettingGroup
        title={t('Comment marche le simulateur')}
        summary={t('Rien n’est envoyé sur WhatsApp · fonctionne même assistant éteint')}
      >
        <p className="text-[12.5px] text-gray-700 leading-snug">
          {t('Discutez avec l’assistant comme si vous étiez un client. Les réponses sont exactement celles qu’un vrai client recevrait.')}
        </p>
        <p className="text-[11.5px] text-gray-500 mt-1.5 leading-snug">
          {t('Rien n’est envoyé sur WhatsApp et rien n’est écrit dans une vraie conversation. Vos fichiers d’essai sont gardés à part, en privé. Fonctionne même si l’assistant est éteint.')}
        </p>
      </SettingGroup>

      <ScenarioTests />

      {/* ── Whose state are we simulating against? ──────────────────────────
          ⚠️ THE BANNER ABOVE PROMISES « exactement celles qu'un vrai client
          recevrait ». That is only true once a customer is picked: without one
          the model gets the prompt and the conversation and none of the
          fifteen fact blocks the live agent receives. The default therefore
          says so plainly rather than letting the banner speak for it. */}
      <div className={`rounded-xl border px-4 py-3 ${
        simChatId ? 'border-gray-200 bg-white' : 'border-amber-200 bg-amber-50'
      }`}>
        <label className="block text-[12.5px] font-semibold text-gray-800 mb-1.5">
          {t('Simuler pour un client')}
        </label>
        <select
          value={simChatId ?? ''}
          onChange={(e) => setSimChatId(e.target.value || null)}
          className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-[12.5px] bg-white
                     focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
        >
          <option value="">{t('Aucun — réponse générique, sans les données du client')}</option>
          {(chats.data?.conversations ?? []).map((cv) => (
            <option key={cv.chat_id} value={cv.chat_id}>
              {(cv.name || cv.chat_id.split('@')[0])}
              {cv.last_body ? ` — ${cv.last_body.slice(0, 40)}` : ''}
            </option>
          ))}
        </select>
        <p className={`text-[11px] mt-1.5 leading-snug ${simChatId ? 'text-green-700' : 'text-amber-700'}`}>
          {simChatId
            ? t('L’assistant verra la commande, les photos reçues, l’étape en cours et les réclamations de ce client — comme en vrai.')
            : t('Sans client choisi, l’assistant ne voit ni commande ni photos : la réponse ne sera pas celle qu’il enverrait vraiment.')}
        </p>
        {chats.isLoading && (
          <p className="text-[11px] text-gray-400 mt-1">{t('Chargement…')}</p>
        )}
        {/* ⚠️ The line above is NEVER collapsed. It is not explanation — it
            says whether what you are about to read is the reply a customer
            would actually receive. Hiding it would let the simulator lie
            quietly, which is worse than not having a simulator. */}
      </div>

      {/* The Conversations thread itself (2026-10-01) — see the header. */}
      <div className="mx-auto w-full max-w-3xl">
        <ThreadFrame
          resetKey="simulator"
          title={simName ? `${t('Simulation')} · ${simName}` : t('Simulation')}
          messages={messages}
          options={options}
          empty={(
            <p className="text-center py-10">
              <span className="text-[12.5px] rounded-lg px-3 py-1.5 bg-[var(--wa-empty)] text-[var(--wa-chip-ink)]">
                {t('Écrivez un message comme le ferait un client.')}
              </span>
            </p>
          )}
          after={simMut.isPending ? (
            <div className="flex justify-end mt-2.5" aria-live="polite">
              <span className="rounded-lg px-3 py-1.5 text-[13px] italic bg-[var(--wa-out)] text-[var(--wa-meta)] shadow-[0_1px_0.5px_rgba(11,20,26,0.13)]">
                {t('écrit…')}
              </span>
            </div>
          ) : null}
          composer={(
            <Composer
              chatId="simulator"
              onSend={send}
              busy={simMut.isPending}
              hint={<>{t('Simulation : rien ne part sur WhatsApp.')} · ⌘/Ctrl + Entrée · {t('coller une image : ⌘/Ctrl + V')}</>}
            />
          )}
        />
      </div>

      {simMut.isError && (
        <p className="text-[11.5px] text-red-600 text-center" role="alert">{(simMut.error as Error).message}</p>
      )}

      {/* ── Correction panel. Progressive disclosure: only appears once a reply
             is picked, so the simulator stays a simple chat until you need it. */}
      {correctingTurn && (
        <div className="mx-auto w-full max-w-[560px] bg-white border border-brand-200 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-gray-800 mb-1">{t('Corriger cette réponse')}</h3>
          <p className="text-[11.5px] text-gray-500 mb-3 leading-snug">
            {t('Dites simplement ce qu’il aurait dû faire, dans vos mots — comme une consigne. Ou réécrivez le message complet. L’assistant en tire la règle ET vous montre le message qu’elle produit, avant que vous validiez.')}
          </p>

          <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1">{t('Il a dit')}</p>
          <p className="text-[12px] text-gray-500 bg-gray-50 border border-gray-100 rounded-lg px-3 py-2 mb-3 whitespace-pre-wrap">
            {correctingTurn.content}
          </p>

          <label htmlFor="sim-correction" className="block text-[11px] font-semibold text-gray-500 mb-1">
            {t('Ce qu’il aurait dû faire')}
          </label>
          <textarea
            id="sim-correction"
            className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-[13px] min-h-[110px] bg-white transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
            value={correction} onChange={(e) => setCorrection(e.target.value)}
            placeholder={t('Ex. au-dessus de Trio, 299 Dh par album. Réponds court.')}
          />

          <div className="flex items-center gap-3 mt-3 flex-wrap">
            <Button size="sm" loading={teachMut.isPending}
              disabled={!correction.trim()}
              onClick={() => teachMut.mutate(correctingTurn.id)}>
              {t('Apprendre de cette correction')}
            </Button>
            <button className="text-[11.5px] text-gray-500 underline cursor-pointer"
              onClick={() => { setCorrecting(null); setProposal(null); setPreview(null); }}>
              {t('Annuler')}
            </button>
            {teachMut.isError && (
              <span className="text-[11.5px] text-red-600" role="alert">{(teachMut.error as Error).message}</span>
            )}
          </div>

          {/* The proposal is shown for approval, never applied on its own. */}
          {proposal && proposal.mode === 'skip' && (
            <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5">
              <p className="text-[12px] text-gray-700">
                {t('Aucune règle à ajouter — c’est une reformulation de style, pas une nouvelle consigne.')}
              </p>
              {proposal.reason && <p className="text-[11px] text-gray-500 mt-1">{proposal.reason}</p>}
            </div>
          )}

          {proposal && proposal.mode !== 'skip' && (
            <div className="mt-4 rounded-xl border border-brand-200 bg-brand-50 px-4 py-3">
              <p className="text-[11px] font-semibold text-brand-700 uppercase tracking-wide">
                {proposal.mode === 'update' ? t('Règle à modifier') : t('Nouvelle règle proposée')}
              </p>
              {proposal.reason && (
                <p className="text-[11.5px] text-brand-800 mt-1 italic">{proposal.reason}</p>
              )}
              <p className="text-[13px] font-semibold text-gray-800 mt-2">{proposal.title}</p>
              <textarea
                className="w-full mt-1 px-3 py-2 border border-brand-200 rounded-lg text-[12.5px] min-h-[90px] bg-white focus:outline-none focus:ring-2 focus:ring-brand-500"
                value={proposal.body}
                onChange={(e) => setProposal({ ...proposal, body: e.target.value })}
              />
              {preview && (
                <div className="mt-3">
                  <p className="text-[11px] font-semibold text-brand-700 uppercase tracking-wide mb-1">
                    {t('Ce qu’il dirait avec cette règle')}
                  </p>
                  {/* Same bubble styling as the chat, so you judge it as the
                      customer sees it rather than as a block of admin text. */}
                  <p className="text-[12.5px] text-gray-900 bg-white border border-gray-200 rounded-lg px-3 py-2 whitespace-pre-wrap">
                    {preview}
                  </p>
                </div>
              )}
              <div className="flex items-center gap-3 mt-2.5 flex-wrap">
                <Button size="sm" loading={applyMut.isPending} onClick={() => applyMut.mutate()}>
                  {proposal.mode === 'update' ? t('Remplacer la règle') : t('Ajouter au cerveau')}
                </Button>
                <button className="text-[11.5px] text-gray-500 underline cursor-pointer" onClick={() => setProposal(null)}>
                  {t('Ne pas ajouter')}
                </button>
                {applyMut.isError && (
                  <span className="text-[11.5px] text-red-600" role="alert">{(applyMut.error as Error).message}</span>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {turns.length > 0 && (
        <div className="text-center">
          <button
            className="text-[11.5px] text-gray-500 underline cursor-pointer px-2 py-2"
            onClick={() => {
              // Explicit end, because the thread now survives tab switches and
              // reloads — it has to be closed on purpose, not by navigating away.
              if (window.confirm(t('Terminer cette conversation de test ?'))) {
                setTurns([]); setCorrecting(null); setProposal(null);
                try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
              }
            }}
          >
            {t('Terminer la conversation')}
          </button>
        </div>
      )}
    </div>
  );
}
